import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Button,
  Space,
  Avatar,
  Divider,
  Timeline,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Upload,
  List,
  Spin,
  Tabs,
  Empty,
  Alert,
  App,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  PhoneOutlined,
  MailOutlined,
  FileTextOutlined,
  InboxOutlined,
  ArrowLeftOutlined,
  DollarOutlined,
  DownloadOutlined,
  FileOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
  WhatsAppOutlined,
  SendOutlined,
  PhoneFilled,
  AudioOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  CustomerServiceOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import useDealStore from "../../store/dealStore";
import useAuthStore from "../../store/authStore";
import useSettingsStore from "../../store/settingsStore";
import apiService from "../../services/apiService";
import whatsappSettingsService from "../../services/whatsappSettingsService";
import dayjs from "dayjs";
import { v4 as uuidv4 } from 'uuid';
import useManualCalling from "../../hooks/useManualCalling";
import PhoneSelectionModal from "../Calls/PhoneSelectionModal";
import CallControlButtons from "../Calls/CallControlButtons";
import callLogService from "../../services/callLogService";
import EmailCompose from "../Leads/EmailCompose";
import EmailHistory from "../Leads/EmailHistory";
import { Drawer, Progress, Descriptions, Popconfirm } from "antd";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Check if WhatsApp feature is enabled via environment variable
const isWhatsAppEnabled = process.env.REACT_APP_WHATSAPP_ENABLED === 'true';

/* Check if AI Calling feature is enabled via environment variable */
const isAICallingEnabled = process.env.REACT_APP_AI_CALLING_ENABLED === 'true';

const DealDetailView = () => {
  // Use only App message API to avoid duplicate `message` imports and name conflicts
  const { message: messageApi } = App.useApp();
  const { id } = useParams();
  const navigate = useNavigate();
  
  // State for WhatsApp channel ID fetched from database
  const [whatsappChannelId, setWhatsappChannelId] = useState('');
  const {
    currentDeal,
    fetchDealById,
    updateDeal,
    addNote,
    updateNote,
    deleteNote,
    addActivity,
    addRequirement,
    updateRequirement,
    deleteRequirement,
    addMeeting,
    updateMeeting,
    deleteMeeting,
    addCall,
    updateCall,
    deleteCall,
    addDocument,
    deleteDocument,
    addTask,
    updateTask,
    deleteTask,
    isLoading,
  } = useDealStore();
  const { user } = useAuthStore();
  const formatCurrency = useSettingsStore((s) => s.formatCurrency);
  const currencySymbol = useSettingsStore((s) => s.currencySymbol);

  console.log("Current Deal:", currentDeal);

  const [activeTab, setActiveTab] = useState("requirements");
  const [noteModalVisible, setNoteModalVisible] = useState(false);
  const [activityModalVisible, setActivityModalVisible] = useState(false);
  const [requirementModalVisible, setRequirementModalVisible] = useState(false);
  const [meetingModalVisible, setMeetingModalVisible] = useState(false);
  const [callModalVisible, setCallModalVisible] = useState(false);
  const [documentModalVisible, setDocumentModalVisible] = useState(false);
  const [taskModalVisible, setTaskModalVisible] = useState(false);
  const [winDealModalVisible, setWinDealModalVisible] = useState(false);
  const [lostDealModalVisible, setLostDealModalVisible] = useState(false);
  const [assignDealModalVisible, setAssignDealModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [activityType, setActivityType] = useState("call");
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
  const [noteForm] = Form.useForm();
  const [activityForm] = Form.useForm();
  const [requirementForm] = Form.useForm();
  const [meetingForm] = Form.useForm();
  const [callForm] = Form.useForm();
  const [taskForm] = Form.useForm();
  const [winDealForm] = Form.useForm();
  const [lostDealForm] = Form.useForm();
  const [assignForm] = Form.useForm();

  const [isCalling, setIsCalling] = useState(false); // New state for AI calling
  const [transcripts, setTranscripts] = useState([]); // New state for transcripts
  const [isTranscriptsLoading, setIsTranscriptsLoading] = useState(false); // New state for transcripts loading
  const [whatsappUnavailable, setWhatsappUnavailable] = useState(false);

  // AI Call phone selection modal state
  const [aiCallPhoneModalVisible, setAICallPhoneModalVisible] = useState(false);
  const [aiCallSelectedCountryIndex, setAICallSelectedCountryIndex] = useState(0);
  const [aiCallSelectedPhoneIndex, setAICallSelectedPhoneIndex] = useState(0);
  const [isInitiatingAICall, setIsInitiatingAICall] = useState(false);

  // WhatsApp chat states (mirror LeadDetailView)
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const chatContainerRef = useRef(null);

  // Call Logs state
  const [callLogs, setCallLogs] = useState([]);
  const [isLoadingCallLogs, setIsLoadingCallLogs] = useState(false);

  // Email state
  const [emailRefreshTrigger, setEmailRefreshTrigger] = useState(0);

  // Proposal state
  const [proposalDrawerVisible, setProposalDrawerVisible] = useState(false);
  const [proposalPreviewVisible, setProposalPreviewVisible] = useState(false);
  const [proposalPreviewData, setProposalPreviewData] = useState(null);
  const [editedProposalContent, setEditedProposalContent] = useState('');
  const [editedCompanyInfo, setEditedCompanyInfo] = useState(null);
  const [isGeneratingProposal, setIsGeneratingProposal] = useState(false);
  const [proposalForm] = Form.useForm();
  const [uploadingFiles, setUploadingFiles] = useState([]);

  // Manual calling hook
  const {
    callSettings,
    isLoadingCallSettings,
    phoneSelectionModalVisible,
    selectedCountryIndex,
    selectedPhoneIndex,
    isCalling: isManualCalling,
    activeCallUUID,
    fetchCallSettings,
    openPhoneSelectionModal,
    closePhoneSelectionModal,
    setSelectedCountryIndex,
    setSelectedPhoneIndex,
    setIsCalling: setIsManualCalling,
    setActiveCallUUID,
    initiateCall,
    endCall,
    toggleMute,
    toggleSpeaker,
  } = useManualCalling(messageApi);

  useEffect(() => {
    if (id) {
      fetchDealById(id);
      loadTeamMembers();
      fetchCallSettings(); // Fetch call settings on mount
    }
  }, [id, fetchDealById, fetchCallSettings]);

  // Fetch WhatsApp settings from database
  useEffect(() => {
    const fetchWhatsAppSettings = async () => {
      try {
        console.log('[fetchWhatsAppSettings] Starting to fetch WhatsApp settings');
        const response = await whatsappSettingsService.getWhatsappSettings();
        if (response.success && response.data) {
          console.log('[fetchWhatsAppSettings] WhatsApp settings:', response.data);
          setWhatsappChannelId(response.data.whatsappChannelId || '');
        } else {
          console.log('[fetchWhatsAppSettings] No WhatsApp settings found');
          setWhatsappChannelId('');
        }
      } catch (error) {
        console.error('[fetchWhatsAppSettings] Error fetching WhatsApp settings:', error);
        setWhatsappChannelId('');
      }
    };

    if (isWhatsAppEnabled) {
      fetchWhatsAppSettings();
    }
  }, []);

  // Initialize Plivo Browser SDK (WebRTC) once on app mount
  useEffect(() => {
    const initializePlivo = async () => {
      // Try to fetch credentials from backend first
      let sipUser = null;
      let sipPassword = null;

      try {
        const callSettingsService = (await import('../../services/callSettingsService')).default;
        const credentials = await callSettingsService.getPlivoCredentials();
        if (credentials) {
          sipUser = credentials.sipUser;
          sipPassword = credentials.sipPassword;
        }
      } catch (error) {
        console.error('Error fetching Plivo credentials from backend:', error);
      }

      // Fallback to environment variables if backend credentials not available
      if (!sipUser || !sipPassword) {
        sipUser = process.env.REACT_APP_PLIVO_SIP_USER;
        sipPassword = process.env.REACT_APP_PLIVO_SIP_PASSWORD;
      }

      if (!window.Plivo) {
        console.warn('[Plivo] Browser SDK not loaded yet.');
        return;
      }
      if (!sipUser || !sipPassword) {
        console.warn('[Plivo] SIP credentials not available from backend or environment. Skipping login.');
        return;
      }

      try {
        const options = {
          debug: 'INFO',
          permOnClick: true,
          enableQualityTracking: 'all',
          closeProtection: true,
        };
        // Initialize Browser SDK v2
        const plivoBrowserSdk = new window.Plivo(options);

        // Register events on the client
        plivoBrowserSdk.client.on('onLogin', () => console.log('[Plivo] Logged in'));
        plivoBrowserSdk.client.on('onLoginFailed', (err) => console.error('[Plivo] Login failed:', err));
        plivoBrowserSdk.client.on('onLogout', () => console.log('[Plivo] Logged out'));

        plivoBrowserSdk.client.on('onIncomingCall', (callerID, extraHeaders, callInfo, callerName) => {
          console.log('[Plivo] Incoming call:', { callerID, callerName, callInfo, extraHeaders });
          try {
            plivoBrowserSdk.client.answer(callInfo?.callUUID);
          } catch (e) {
            console.error('[Plivo] Failed to answer call:', e);
          }
        });

        plivoBrowserSdk.client.on('onCallAnswered', (callInfo) => {
          console.log('[Plivo] Call answered:', callInfo);
          messageApi.destroy();
          messageApi.success('Call connected');
        });

        plivoBrowserSdk.client.on('onCallConnected', (callInfo) => {
          console.log('[Plivo] Call connected:', callInfo);
          messageApi.destroy();
        });

        plivoBrowserSdk.client.on('onCallTerminated', (hangupInfo, callInfo) => {
          console.log('[Plivo] Call ended:', { hangupInfo, callInfo });
          messageApi.destroy();
          messageApi.info('Call ended');
          setIsManualCalling(false);
          setActiveCallUUID(null);
          window._plivo.client.hangup();
        });

        plivoBrowserSdk.client.on('onMediaError', (err) => {
          console.error('[Plivo] Media error:', err);
          messageApi.destroy();
          messageApi.error('Call disconnected due to media error');
          setIsManualCalling(false);
          setActiveCallUUID(null);
          window._plivo.client.hangup();
        });

        plivoBrowserSdk.client.on('onCallFailed', (reason, callInfo) => {
          console.error('[Plivo] Call failed:', { reason, callInfo });
          messageApi.destroy();
          messageApi.error('Call failed: ' + reason);
          setIsManualCalling(false);
          setActiveCallUUID(null);
          window._plivo.client.hangup();
        });

        plivoBrowserSdk.client.on('onCallRejected', (callInfo) => {
          console.log('[Plivo] Call rejected by remote party:', callInfo);
          messageApi.destroy();
          messageApi.info('Call rejected by other party');
          setIsManualCalling(false);
          setActiveCallUUID(null);
          window._plivo.client.hangup();
        });

        plivoBrowserSdk.client.on('onRemoteDisconnect', (callInfo) => {
          console.log('[Plivo] Remote party disconnected:', callInfo);
          messageApi.destroy();
          messageApi.info('Other party ended the call');
          setIsManualCalling(false);
          setActiveCallUUID(null);
          window._plivo.client.hangup();
        });

        // Expose globally for debugging/usage in other components if needed
        window._plivo = plivoBrowserSdk;

        // Login using SIP Endpoint credentials
        plivoBrowserSdk.client.login(sipUser, sipPassword);
        console.log('[Plivo] Initializing and logging in as', sipUser);
      } catch (e) {
        console.error('[Plivo] Initialization error:', e);
      }
    };

    initializePlivo();
  }, [messageApi, setIsManualCalling, setActiveCallUUID]);

  useEffect(() => {
    const hasChannel = whatsappChannelId;
    if (activeTab === 'whatsapp' && (hasChannel || currentDeal?.contact?.phone)) {
      fetchChatMessages();
    } else if (activeTab === 'aicalling' && currentDeal?.campaignIds?.length > 0) {
      fetchTranscripts();
    }
  }, [activeTab, currentDeal?.contact?.phone, currentDeal?.campaignIds, whatsappChannelId]);

  // Poll for new WhatsApp messages while the WhatsApp tab is active.
  useEffect(() => {
    if (activeTab !== 'whatsapp') return undefined;
    if (whatsappUnavailable) return undefined;
    if (!whatsappChannelId && !currentDeal?.contact?.phone) return undefined;

    const intervalId = setInterval(() => {
      fetchChatMessages();
    }, 8000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, whatsappUnavailable, whatsappChannelId, currentDeal?.contact?.phone]);

  // Auto-scroll to bottom when messages change and WhatsApp tab is active
  useEffect(() => {
    if (activeTab === 'whatsapp' && chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages, activeTab]);

  // Fetch call logs when tab changes to 'calllogs'
  useEffect(() => {
    const fetchCallLogsData = async () => {
      if (activeTab === 'calllogs' && currentDeal?.contact?.phone) {
        setIsLoadingCallLogs(true);
        try {
          const response = await callLogService.getCallLogs(currentDeal.contact.phone);
          if (response.success) {
            setCallLogs(response.data);
          } else {
            messageApi.error('Failed to fetch call logs');
          }
        } catch (error) {
          console.error('Error fetching call logs:', error);
          messageApi.error('Failed to fetch call logs');
        } finally {
          setIsLoadingCallLogs(false);
        }
      }
    };
    fetchCallLogsData();
  }, [activeTab, currentDeal?.contact?.phone]);

  const handleStartAICallCampaign = async () => {
    if (!currentDeal?._id) {
      messageApi.error("Deal ID is missing. Cannot start AI call campaign.");
      return;
    }

    // Validate call settings are configured
    if (!callSettings?.plivoSettings?.countries || callSettings.plivoSettings.countries.length === 0) {
      messageApi.error("Call settings are not configured. Please configure call settings first.");
      return;
    }

    // Open phone selection modal
    setAICallSelectedCountryIndex(0);
    setAICallSelectedPhoneIndex(0);
    setAICallPhoneModalVisible(true);
  };

  const handleConfirmAICallPhoneSelection = async () => {
    if (!currentDeal?._id) {
      messageApi.error("Deal ID is missing. Cannot start AI call campaign.");
      return;
    }

    const selectedCountry = callSettings?.plivoSettings?.countries?.[aiCallSelectedCountryIndex];
    if (!selectedCountry) {
      messageApi.error("Invalid country selection.");
      return;
    }

    const selectedPhoneNumber = selectedCountry.phoneNumbers?.[aiCallSelectedPhoneIndex];
    if (!selectedPhoneNumber) {
      messageApi.error("Invalid phone number selection.");
      return;
    }

    // Normalize selected phone number before sending to backend
    const aiCallDigitsOnly = String(selectedPhoneNumber).replace(/\D/g, '');
    if (!aiCallDigitsOnly) {
      messageApi.error("Invalid phone number. Please check the number and try again.");
      return;
    }

    setIsInitiatingAICall(true);
    try {
      const payload = {
        phoneNumber: aiCallDigitsOnly,
        countryCode: selectedCountry.countryCode,
      };

      const response = await apiService.post(
        `/deals/${currentDeal._id}/start-aicall-campaign`,
        payload
      );

      if (response.data.code === "AICALL_CAMPAIGN_STARTED") {
        messageApi.success(response.data.message);
        setAICallPhoneModalVisible(false);
        fetchDealById(id); // Refresh data to get new campaignIds
      } else {
        messageApi.error(response.data.message || "Failed to start AI call campaign.");
      }
    } catch (error) {
      console.error("Error starting AI call campaign:", error);
      messageApi.error(error.response?.data?.message || "Failed to start AI call campaign.");
    } finally {
      setIsInitiatingAICall(false);
    }
  };

  const fetchTranscripts = async () => {
    if (!currentDeal?.campaignIds || currentDeal.campaignIds.length === 0) {
      setTranscripts([]);
      return;
    }

    setIsTranscriptsLoading(true);
    try {
      const response = await apiService.post('/pitchbot/transcripts', {
        ids: currentDeal.campaignIds,
      });
      if (response.status === 200) {
        setTranscripts(response.data);
      } else {
        messageApi.error(response.data.message || "Failed to fetch transcripts.");
      }
    } catch (error) {
      console.error("Error fetching transcripts:", error);
      messageApi.error(error.response?.data?.message || "Failed to fetch transcripts.");
    } finally {
      setIsTranscriptsLoading(false);
    }
  };

  // WhatsApp chat handlers (reuse same endpoints as Lead)
  const fetchChatMessages = async () => {
    const resolvedChannelId = whatsappChannelId;
    const phone = currentDeal?.contact?.phone;
    if (!resolvedChannelId && !phone) {
      messageApi.warning("Deal does not have a default channel ID or contact phone for WhatsApp chat.");
      return;
    }
    setIsChatLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (resolvedChannelId) queryParams.append('channelId', resolvedChannelId);
      if (phone) queryParams.append('phoneNumber', phone);
      const response = await apiService.get(`/chat/messages?${queryParams.toString()}`);
      if (response.data.success) {
        setWhatsappUnavailable(false);
        setChatMessages(response.data.data.messages);
      } else {
        if (response.data.code === "WHATSAPP_CONFIG_MISSING" || response.data.code === "WHATSAPP_PROVIDER_UNAVAILABLE") {
          setWhatsappUnavailable(true);
        }
        messageApi.error(response.data.message || "Failed to fetch chat messages.");
      }
    } catch (error) {
      console.error("Failed to fetch chat messages:", error);
      const serverCode = error?.response?.data?.code;
      if (serverCode === "WHATSAPP_CONFIG_MISSING" || serverCode === "WHATSAPP_PROVIDER_UNAVAILABLE") {
        setWhatsappUnavailable(true);
        messageApi.error(error.response?.data?.message || "WhatsApp integration is unavailable.");
      } else {
        messageApi.error("Failed to fetch chat messages.");
      }
    } finally {
      setIsChatLoading(false);
    }
  };

  const handleSendChatMessage = async () => {
    if (!chatInput.trim()) return;
    const resolvedChannelId = whatsappChannelId;
    const phone = currentDeal?.contact?.phone;
    if (!resolvedChannelId || !phone) {
      messageApi.error("Cannot send message: Missing default channel ID or contact phone.");
      return;
    }
    if (whatsappUnavailable) {
      messageApi.error("WhatsApp integration is currently unavailable.");
      return;
    }

    const contactName = currentDeal?.contact
      ? `${currentDeal.contact.firstName || ''} ${currentDeal.contact.lastName || ''}`.trim() || (currentDeal.title || 'Deal Contact')
      : (currentDeal.title || 'Deal Contact');

    // Optimistic message
    const newMessage = {
      _id: uuidv4(),
      entity: 'bot',
      message: chatInput,
      timestamp: new Date().toISOString(),
      channelId: resolvedChannelId,
      contactName,
      recipientPhone: phone,
      whatsappType: 'text',
    };
    setChatMessages((prev) => [...prev, newMessage]);
    setChatInput('');

    try {
      const response = await apiService.post('/chat/whatsapp/send', {
        channelId: resolvedChannelId,
        recipientPhone: phone,
        message: newMessage.message,
        contactName,
      });
      if (response.data.success) {
        messageApi.success("Message sent successfully!");
        fetchChatMessages();
      } else {
        if (response.data.code === "WHATSAPP_CONFIG_MISSING" || response.data.code === "WHATSAPP_PROVIDER_UNAVAILABLE") {
          setWhatsappUnavailable(true);
        }
        messageApi.error(response.data.message || "Failed to send message.");
        setChatMessages((prev) => prev.filter((m) => m._id !== newMessage._id));
      }
    } catch (error) {
      const serverMessage = error?.response?.data?.message;
      const upstreamStatus = error?.response?.data?.upstreamStatus;
      const serverCode = error?.response?.data?.code;
      console.error("Error sending chat message:", {
        error,
        serverMessage,
        upstreamStatus,
        response: error?.response?.data,
      });
      if (serverCode === "WHATSAPP_CONFIG_MISSING" || serverCode === "WHATSAPP_PROVIDER_UNAVAILABLE") {
        setWhatsappUnavailable(true);
      }
      messageApi.error(serverMessage || error?.message || "Failed to send message.");
      setChatMessages((prev) => prev.filter((m) => m._id !== newMessage._id));
    }
  };

  const handleGenerateProposal = async (values) => {
    try {
      setIsGeneratingProposal(true);
      
      // Call API to generate proposal preview with extended timeout for AI processing
      const response = await apiService.post(`/deals/${id}/generate-proposal-preview`, values, {
        timeout: 60000 // 60 seconds for AI generation
      });
      
      if (response.data.success) {
        // Store preview data and show preview modal
        setProposalPreviewData({
          ...response.data.data,
          originalFormValues: values
        });
        setEditedProposalContent(response.data.data.proposalContent);
        setEditedCompanyInfo(response.data.data.companyInfo);
        setProposalPreviewVisible(true);
        setProposalDrawerVisible(false); // Close the form drawer
        messageApi.success("Proposal preview generated! Review and edit before creating PDF.");
      } else {
        messageApi.error(response.data.message || "Failed to generate proposal preview");
      }
    } catch (error) {
      console.error("Error generating proposal preview:", error);
      messageApi.error(error.response?.data?.message || "Failed to generate proposal preview");
    } finally {
      setIsGeneratingProposal(false);
    }
  };

  const handleConfirmProposal = async () => {
    try {
      setIsGeneratingProposal(true);
      
      // Call API to generate final PDF with edited content
      const response = await apiService.post(`/deals/${id}/generate-proposal`, {
        ...proposalPreviewData.originalFormValues,
        proposalContent: editedProposalContent,
        companyInfo: editedCompanyInfo
      }, {
        timeout: 60000
      });
      
      if (response.data.success) {
        messageApi.success("Proposal PDF generated successfully!");
        
        // Download the PDF
        const base64Data = response.data.data.pdfBase64;
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const pdfBlob = new Blob([byteArray], { type: 'application/pdf' });
        
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Proposal_${currentDeal?.title || 'Deal'}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        // Close preview modal and reset
        setProposalPreviewVisible(false);
        setProposalPreviewData(null);
        proposalForm.resetFields();
      } else {
        messageApi.error(response.data.message || "Failed to generate proposal PDF");
      }
    } catch (error) {
      console.error("Error generating proposal PDF:", error);
      messageApi.error(error.response?.data?.message || "Failed to generate proposal PDF");
    } finally {
      setIsGeneratingProposal(false);
    }
  };

  const loadTeamMembers = async () => {
    setLoadingTeamMembers(true);
    try {
      const response = await apiService.get("/users/team-members");
      if (response.data.success) {
        setTeamMembers(response.data.data.teamMembers);
      }
    } catch (error) {
      console.error("Failed to load team members:", error);
    } finally {
      setLoadingTeamMembers(false);
    }
  };

  const handleAddNote = async (values) => {
    const result = await addNote(id, values.content);
    if (result.success) {
      messageApi.success("Note added successfully");
      setNoteModalVisible(false);
      noteForm.resetFields();
      fetchDealById(id); // Refresh data
    } else {
      messageApi.error(result.error || "Failed to add note");
    }
  };

  const handleAddActivity = async (values) => {
    const result = await addActivity(id, {
      type: activityType,
      description: values.description,
      date: new Date(),
    });
    if (result.success) {
      messageApi.success("Activity added successfully");
      setActivityModalVisible(false);
      activityForm.resetFields();
      fetchDealById(id); // Refresh deal data to update activity timeline
    } else {
      messageApi.error(result.error || "Failed to add activity");
    }
  };

  const handleStageChange = async (newStage) => {
    const result = await updateDeal(id, { stage: newStage });
    if (result.success) {
      messageApi.success("Deal stage updated successfully");
    } else {
      messageApi.error(result.error || "Failed to update deal stage");
    }
  };

  const handleWinDeal = async (values) => {
    const dealData = {
      stage: "closed_won",
      closedAt: new Date().toISOString(),
      closingRemarks: values.remarks,
      actualValue: values.actualValue || currentDeal.dealValue,
      closingReason: values.reason,
    };

    const result = await updateDeal(id, dealData);
    if (result.success) {
      messageApi.success("Deal marked as Won successfully!");
      setWinDealModalVisible(false);
      winDealForm.resetFields();
    } else {
      messageApi.error(result.error || "Failed to mark deal as won");
    }
  };

  const handleLostDeal = async (values) => {
    const dealData = {
      stage: "closed_lost",
      closedAt: new Date().toISOString(),
      closingRemarks: values.remarks,
      lostReason: values.reason,
      competitorName: values.competitor,
    };

    const result = await updateDeal(id, dealData);
    if (result.success) {
      messageApi.success("Deal marked as Lost");
      setLostDealModalVisible(false);
      lostDealForm.resetFields();
    } else {
      messageApi.error(result.error || "Failed to mark deal as lost");
    }
  };

  const handleAssignDeal = async (values) => {
    try {
      const result = await updateDeal(id, { assignedTo: values.assignedTo });
      if (result.success) {
        messageApi.success("Deal assignment updated successfully");
        setAssignDealModalVisible(false);
        assignForm.resetFields();
        fetchDealById(id); // Refresh deal data
      } else {
        messageApi.error(result.error || "Failed to update deal assignment");
      }
    } catch (error) {
      messageApi.error("Failed to update deal assignment");
    }
  };

  const handleAddRequirement = async (values) => {
    const result = await addRequirement(id, values);
    if (result.success) {
      messageApi.success("Requirement added successfully");
      setRequirementModalVisible(false);
      requirementForm.resetFields();
      fetchDealById(id); // Refresh data
    } else {
      messageApi.error(result.error || "Failed to add requirement");
    }
  };

  const handleUpdateRequirement = async (values) => {
    const result = await updateRequirement(id, editingItem.item._id, values);
    if (result.success) {
      messageApi.success("Requirement updated successfully");
      setRequirementModalVisible(false);
      requirementForm.resetFields();
      setEditingItem(null);
      fetchDealById(id); // Refresh data
    } else {
      messageApi.error(result.error || "Failed to update requirement");
    }
  };

  const handleDeleteRequirement = async (requirementId) => {
    const result = await deleteRequirement(id, requirementId);
    if (result.success) {
      messageApi.success("Requirement deleted successfully");
      fetchDealById(id); // Refresh data
    } else {
      messageApi.error(result.error || "Failed to delete requirement");
    }
  };

  const handleAddMeeting = async (values) => {
    const result = await addMeeting(id, {
      ...values,
      scheduledAt: values.scheduledAt?.toISOString(),
    });
    if (result.success) {
      messageApi.success("Meeting added successfully");
      setMeetingModalVisible(false);
      meetingForm.resetFields();
      fetchDealById(id); // Refresh data
    } else {
      messageApi.error(result.error || "Failed to add meeting");
    }
  };

  const handleUpdateMeeting = async (values) => {
    const result = await updateMeeting(id, editingItem.item._id, {
      ...values,
      scheduledAt: values.scheduledAt?.toISOString(),
    });
    if (result.success) {
      messageApi.success("Meeting updated successfully");
      setMeetingModalVisible(false);
      meetingForm.resetFields();
      setEditingItem(null);
      fetchDealById(id); // Refresh data
    } else {
      messageApi.error(result.error || "Failed to update meeting");
    }
  };

  const handleDeleteMeeting = async (meetingId) => {
    const result = await deleteMeeting(id, meetingId);
    if (result.success) {
      messageApi.success("Meeting deleted successfully");
      fetchDealById(id); // Refresh data
    } else {
      messageApi.error(result.error || "Failed to delete meeting");
    }
  };

  const handleAddCall = async (values) => {
    const result = await addCall(id, values);
    if (result.success) {
      messageApi.success("Call added successfully");
      setCallModalVisible(false);
      callForm.resetFields();
      fetchDealById(id); // Refresh data
    } else {
      messageApi.error(result.error || "Failed to add call");
    }
  };

  const handleUpdateCall = async (values) => {
    const result = await updateCall(id, editingItem.item._id, values);
    if (result.success) {
      messageApi.success("Call updated successfully");
      setCallModalVisible(false);
      callForm.resetFields();
      setEditingItem(null);
      fetchDealById(id); // Refresh data
    } else {
      messageApi.error(result.error || "Failed to update call");
    }
  };

  const handleDeleteCall = async (callId) => {
    const result = await deleteCall(id, callId);
    if (result.success) {
      messageApi.success("Call deleted successfully");
      fetchDealById(id); // Refresh data
    } else {
      messageApi.error(result.error || "Failed to delete call");
    }
  };

  const handleAddDocument = async (values) => {
    const result = await addDocument(id, values);
    if (result.success) {
      messageApi.success("Document added successfully");
      setDocumentModalVisible(false);
      fetchDealById(id); // Refresh data
    } else {
      messageApi.error(result.error || "Failed to add document");
    }
  };

  const handleDeleteDocument = async (documentId) => {
    const result = await deleteDocument(id, documentId);
    if (result.success) {
      messageApi.success("Document deleted successfully");
    } else {
      messageApi.error(result.error || "Failed to delete document");
    }
  };

  // Task handlers
  const handleAddTask = () => {
    setEditingTask(null);
    taskForm.resetFields();
    setTaskModalVisible(true);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    taskForm.setFieldsValue({
      title: task.title,
      description: task.description,
      priority: task.priority,
      status: task.status,
      dueDate: task.dueDate ? dayjs(task.dueDate) : null,
      notes: task.notes,
    });
    setTaskModalVisible(true);
  };

  const handleDeleteTask = (taskId) => {
    Modal.confirm({
      title: "Delete Task",
      content: "Are you sure you want to delete this task?",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        const result = await deleteTask(id, taskId);
        if (result.success) {
          messageApi.success("Task deleted successfully");
          fetchDealById(id); // Refresh deal data to update tasks list and timeline
        } else {
          messageApi.error(result.error || "Failed to delete task");
        }
      },
    });
  };

  const handleSaveTask = async (values) => {
    const taskData = {
      title: values.title,
      description: values.description || "",
      priority: values.priority || "medium",
      status: values.status || "pending",
      dueDate: values.dueDate ? values.dueDate.toISOString() : null,
      notes: values.notes || "",
      assignedTo: values.assignedTo || null,
    };

    let result;
    if (editingTask) {
      result = await updateTask(id, editingTask._id, taskData);
    } else {
      result = await addTask(id, taskData);
    }

    if (result.success) {
      messageApi.success(
        `Task ${editingTask ? "updated" : "created"} successfully`
      );
      setTaskModalVisible(false);
      taskForm.resetFields();
      setEditingTask(null);
      fetchDealById(id); // Refresh deal data to update tasks list and timeline
    } else {
      // Display validation errors if available
      if (result.details && Array.isArray(result.details)) {
        result.details.forEach((detail) => {
          messageApi.error(detail);
        });
      } else {
        messageApi.error(
          result.error || `Failed to ${editingTask ? "update" : "create"} task`
        );
      }
    }
  };

  const handleDeleteItem = async (type, itemId) => {
    Modal.confirm({
      title: `Delete ${type}?`,
      content: `Are you sure you want to delete this ${type}?`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        switch (type) {
          case "requirement":
            await handleDeleteRequirement(itemId);
            break;
          case "meeting":
            await handleDeleteMeeting(itemId);
            break;
          case "call":
            await handleDeleteCall(itemId);
            break;
          case "document":
            await handleDeleteDocument(itemId);
            break;
          case "note":
            try {
              const result = await deleteNote(id, itemId);
              if (result.success) {
                messageApi.success('Note deleted successfully');
                await fetchDealById(id); // Refresh deal data
              } else {
                messageApi.error(result.error || 'Failed to delete note');
              }
            } catch (error) {
              messageApi.error('Failed to delete note');
              console.error('Error deleting note:', error);
            }
            break;
          default:
            messageApi.error("Unknown item type");
        }
      },
    });
  };

  const handleEditItem = (type, item) => {
    setEditingItem({ type, item });
    // Set the appropriate modal visible based on type
    switch (type) {
      case "requirement":
        requirementForm.setFieldsValue(item);
        setRequirementModalVisible(true);
        break;
      case "meeting":
        meetingForm.setFieldsValue({
          ...item,
          scheduledAt: item.scheduledAt ? dayjs(item.scheduledAt) : null,
        });
        setMeetingModalVisible(true);
        break;
      case "call":
        callForm.setFieldsValue(item);
        setCallModalVisible(true);
        break;
      case "note":
        noteForm.setFieldsValue(item);
        setNoteModalVisible(true);
        break;
      default:
        break;
    }
  };

  const getStageColor = (stage) => {
    const colors = {
      opportunity_qualified: "geekblue",
      proposal_in_progress: "blue",
      poc: "cyan",
      proposal_submitted: "orange",
      confirmed_commit: "purple",
      closed_won: "success",
      closed_lost: "error",
      closed_dropped: "default",
    };
    return colors[stage] || "default";
  };

  if (isLoading) {
    return (
      <Spin size="large" className="flex justify-center items-center h-64" />
    );
  }

  if (currentDeal) {
    return (
      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
        {/* Header */}
        <div className="mb-6">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/deals")}
            className="mb-4"
          >
            Back to Deals
          </Button>
          <div className="flex justify-between items-start">
            <div>
              <Title level={2} className="mb-2">
                {currentDeal.title ||
                  `${currentDeal.firstName} ${currentDeal.lastName}`}
              </Title>
              <Text className="text-gray-600 dark:text-gray-400">
                Deal Value:{" "}
                <Text strong className="text-green-600">
                  {formatCurrency(currentDeal.value)}
                </Text>
              </Text>
            </div>
            <Space>
              {user?.role === "tenant_admin" && (
                <div className="flex items-center gap-2">
                  {currentDeal.assignedTo ? (
                    <div className="flex items-center gap-2 px-3 py-1 bg-gray-50 rounded border">
                      <Avatar size="small">
                        {(() => {
                          const assignedToId =
                            currentDeal.assignedTo?._id ||
                            currentDeal.assignedTo;
                          const member = teamMembers.find(
                            (m) => String(m.id) === String(assignedToId)
                          );
                          return (
                            member?.name?.charAt(0)?.toUpperCase() ||
                            currentDeal.assignedTo?.firstName
                              ?.charAt(0)
                              ?.toUpperCase() ||
                            "U"
                          );
                        })()}
                      </Avatar>
                      <Text className="text-sm">
                        {(() => {
                          const assignedToId =
                            currentDeal.assignedTo?._id ||
                            currentDeal.assignedTo;
                          const member = teamMembers.find(
                            (m) => String(m.id) === String(assignedToId)
                          );
                          return (
                            member?.name ||
                            (currentDeal.assignedTo?.firstName &&
                            currentDeal.assignedTo?.lastName
                              ? `${currentDeal.assignedTo.firstName} ${currentDeal.assignedTo.lastName}`
                              : "Unknown User")
                          );
                        })()}
                      </Text>
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => {
                          const assignedToId =
                            currentDeal?.assignedTo?._id ||
                            currentDeal?.assignedTo;
                          assignForm.setFieldsValue({
                            assignedTo: assignedToId,
                          });
                          setAssignDealModalVisible(true);
                        }}
                        className="focus-visible:focus"
                      />
                    </div>
                  ) : (
                    <Button
                      type="default"
                      icon={<UserOutlined />}
                      onClick={() => {
                        assignForm.setFieldsValue({ assignedTo: null });
                        setAssignDealModalVisible(true);
                      }}
                      className="focus-visible:focus"
                    >
                      Assign Deal
                    </Button>
                  )}
                </div>
              )}
              <Select
                value={currentDeal.stage}
                onChange={handleStageChange}
                style={{ width: 150 }}
              >
                <Option value="opportunity_qualified">Opportunity Qualified</Option>
                <Option value="proposal_in_progress">Proposal in Progress</Option>
                <Option value="poc">Proof of Concept (POC)</Option>
                <Option value="proposal_submitted">Proposal Submitted</Option>
                <Option value="confirmed_commit">Confirmed Commit</Option>
                <Option value="closed_won">Closed Won</Option>
                <Option value="closed_lost">Closed Lost</Option>
                <Option value="closed_dropped">Closed Dropped</Option>
              </Select>
              {currentDeal.stage !== "closed_won" &&
                currentDeal.stage !== "closed_lost" &&
                currentDeal.stage !== "closed_dropped" && (
                  <>
                    <Button
                      type="primary"
                      icon={<CheckCircleOutlined />}
                      onClick={() => setWinDealModalVisible(true)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Win Deal
                    </Button>
                    <Button
                      danger
                      icon={<CloseCircleOutlined />}
                      onClick={() => setLostDealModalVisible(true)}
                    >
                      Lost Deal
                    </Button>
                  </>
                )}
            </Space>
          </div>
        </div>

        <Row gutter={24}>
          {/* Column 1: Deal Details (22%) */}
          <Col span={5}>
            <Card className="shadow-soft h-full bg-white dark:bg-gray-800">
              <div className="text-center mb-4">
                <Avatar
                  size={80}
                  icon={<DollarOutlined />}
                  className="bg-green-100 text-green-600 mb-3"
                />
                <Title level={4} className="mb-1">
                  {currentDeal.title || "Deal"}
                </Title>
                <Text type="secondary">
                  {currentDeal.contact.company || "No company"}
                </Text>
              </div>

              <div className="mb-4">
                <Space direction="vertical" size="small" className="w-full">
                  <Tag
                    color={getStageColor(currentDeal.stage)}
                    className="w-full text-center"
                  >
                    {currentDeal.stage?.replace("_", " ").toUpperCase()}
                  </Tag>
                  {currentDeal.probability && (
                    <Tag color="blue" className="w-full text-center">
                      {currentDeal.probability}% WIN PROBABILITY
                    </Tag>
                  )}
                </Space>
              </div>

              <Divider />

              <div className="space-y-3">
                <div className="mb-3">
                  <Text
                    strong
                    className="block mb-1 text-gray-600 dark:text-gray-400"
                  >
                    Value:
                  </Text>
                  <Text className="text-green-600 font-semibold">
                    {formatCurrency(currentDeal.value)}
                  </Text>
                </div>

                {currentDeal.expectedCloseDate && (
                  <div className="mb-3">
                    <Text
                      strong
                      className="block mb-1 text-gray-600 dark:text-gray-400"
                    >
                      Close:
                    </Text>
                    <Text>
                      {dayjs(currentDeal.expectedCloseDate).format(
                        "MMM DD, YYYY"
                      )}
                    </Text>
                  </div>
                )}

                {currentDeal.source && (
                  <div className="mb-3">
                    <Text
                      strong
                      className="block mb-1 text-gray-600 dark:text-gray-400"
                    >
                      Source:
                    </Text>
                    <Text>
                      {currentDeal.source.replace("_", " ").toUpperCase()}
                    </Text>
                  </div>
                )}

                <div className="mb-3">
                  <Text
                    strong
                    className="block mb-1 text-gray-600 dark:text-gray-400"
                  >
                    Created:
                  </Text>
                  <Text>
                    {dayjs(currentDeal.createdAt).format("MMM DD, YYYY")}
                  </Text>
                </div>
              </div>

              <Divider />

              <div className="space-y-2">
                <CallControlButtons
                  isCalling={isManualCalling}
                  onStartCall={() => openPhoneSelectionModal()}
                  onEndCall={endCall}
                  onToggleMute={toggleMute}
                  onToggleSpeaker={toggleSpeaker}
                  loading={isManualCalling && !activeCallUUID}
                  disabled={isLoadingCallSettings || !currentDeal?.contact?.phone}
                />
                <Button
                  icon={<MailOutlined />}
                  block
                  className="focus-visible:focus"
                >
                  Send Email
                </Button>
                <Button
                  icon={<EditOutlined />}
                  block
                  onClick={() => navigate(`/deals/${currentDeal._id}/edit`)}
                  className="focus-visible:focus"
                >
                  Edit Deal
                </Button>
              </div>
            </Card>
          </Col>

          {/* Column 2: Tabs Content (60%) */}
          <Col span={14}>
            <Card className="shadow-soft h-full bg-white dark:bg-gray-800">
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                  {
                    key: "requirements",
                    label: "Requirements",
                    children: (
                      <div
                        className="space-y-4"
                        style={{
                          maxHeight: "500px",
                          overflowY: "auto",
                          paddingRight: "10px",
                        }}
                      >
                        <div className="flex justify-between items-center">
                          <Title level={5} className="mb-0">
                            Requirements
                          </Title>
                          <Button
                            type="primary"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => setRequirementModalVisible(true)}
                            className="focus-visible:focus"
                          >
                            Add Requirement
                          </Button>
                        </div>
                        <List
                          dataSource={currentDeal.requirements || []}
                          locale={{ emptyText: "No requirements added yet" }}
                          renderItem={(requirement) => (
                            <List.Item
                              actions={[
                                <Button
                                  key="edit"
                                  type="text"
                                  size="small"
                                  icon={<EditOutlined />}
                                  onClick={() =>
                                    handleEditItem("requirement", requirement)
                                  }
                                />,
                                <Button
                                  key="delete"
                                  type="text"
                                  size="small"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() =>
                                    handleDeleteItem(
                                      "requirement",
                                      requirement._id
                                    )
                                  }
                                />,
                              ]}
                            >
                              <List.Item.Meta
                                title={
                                  <div className="flex justify-between items-center">
                                    <Text strong>{requirement.title}</Text>
                                    <Tag
                                      color={
                                        requirement.priority === "urgent"
                                          ? "red"
                                          : requirement.priority === "high"
                                          ? "orange"
                                          : requirement.priority === "medium"
                                          ? "blue"
                                          : "green"
                                      }
                                    >
                                      {requirement.priority?.toUpperCase()}
                                    </Tag>
                                  </div>
                                }
                                description={
                                  <div>
                                    <Paragraph className="mb-2">
                                      {requirement.description}
                                    </Paragraph>
                                    {requirement.budget && (
                                      <Text type="secondary">
                                        Budget: {formatCurrency(requirement.budget)}
                                      </Text>
                                    )}
                                  </div>
                                }
                              />
                            </List.Item>
                          )}
                        />
                      </div>
                    ),
                  },
                  {
                    key: "meetings",
                    label: "Meetings",
                    children: (
                      <div
                        className="space-y-4"
                        style={{
                          maxHeight: "500px",
                          overflowY: "auto",
                          paddingRight: "10px",
                        }}
                      >
                        <div className="flex justify-between items-center">
                          <Title level={5} className="mb-0">
                            Meetings
                          </Title>
                          <Button
                            type="primary"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => setMeetingModalVisible(true)}
                            className="focus-visible:focus"
                          >
                            Schedule Meeting
                          </Button>
                        </div>

                        <List
                          dataSource={currentDeal.meetings || []}
                          locale={{ emptyText: "No meetings scheduled" }}
                          renderItem={(meeting) => (
                            <List.Item
                              actions={[
                                <Button
                                  key="edit"
                                  type="text"
                                  size="small"
                                  icon={<EditOutlined />}
                                  onClick={() =>
                                    handleEditItem("meeting", meeting)
                                  }
                                />,
                                <Button
                                  key="delete"
                                  type="text"
                                  size="small"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() =>
                                    handleDeleteItem("meeting", meeting._id)
                                  }
                                />,
                              ]}
                            >
                              <List.Item.Meta
                                avatar={<Avatar icon={<CalendarOutlined />} />}
                                title={meeting.title}
                                description={
                                  <div>
                                    <Text>{meeting.description}</Text>
                                    <br />
                                    <Text type="secondary">
                                      {dayjs(meeting.scheduledAt).format(
                                        "MMM DD, YYYY HH:mm"
                                      )}
                                    </Text>
                                    {meeting.location && (
                                      <>
                                        <br />
                                        <Text type="secondary">
                                          Location: {meeting.location}
                                        </Text>
                                      </>
                                    )}
                                  </div>
                                }
                              />
                            </List.Item>
                          )}
                        />
                      </div>
                    ),
                  },
                  {
                    key: "calllogs",
                    label: (
                      <span>
                        <PhoneFilled /> Call Logs
                      </span>
                    ),
                    children: (
                      <div style={{ maxHeight: "500px", overflowY: "auto", minHeight: "200px" }}>
                        <div className="mb-4">
                          <Title level={5}>Call History</Title>
                        </div>
                        {isLoadingCallLogs ? (
                          <div className="flex justify-center items-center h-32">
                            <Spin />
                          </div>
                        ) : callLogs.length > 0 ? (
                          <div className="space-y-4">
                            {callLogs.map((log) => {
                              // Hide SIP addresses and provider internals from list
                              const showFrom = log.from && !/^sip:/i.test(log.from) && !/phone\.plivo/i.test(log.from);
                              // Hide raw numeric 'to' values (like 916200502473)
                              const showTo = log.to && !/^\+?\d+$/.test(String(log.to));
                              // Hide generic hangup reason 'NORMAL_CLEARING'
                              const showHangup = log.hangupCause && String(log.hangupCause).toUpperCase() !== 'NORMAL_CLEARING';

                              return (
                                <Card 
                                  key={log.callUUID} 
                                  size="small"
                                  className="shadow-sm"
                                  styles={{ body: { padding: '16px' } }}
                                >
                                  <div className="flex items-start gap-4">
                                    {/* Left Column: Icon */}
                                    <div className="flex-shrink-0">
                                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-50">
                                        {log.direction === 'inbound' ? (
                                          <PhoneFilled style={{ color: '#1890ff', fontSize: '18px' }} />
                                        ) : (
                                          <PhoneFilled style={{ color: '#52c41a', fontSize: '18px' }} />
                                        )}
                                      </div>
                                    </div>

                                    {/* Middle Column: Call Details */}
                                    <div className="flex-grow min-w-0">
                                      {/* Header Row */}
                                      <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                          <Text strong>
                                            {log.direction === 'inbound' ? 'Incoming' : 'Outgoing'} Call
                                          </Text>
                                          {log.status && (
                                            <Tag color={log.status === 'completed' ? 'success' : 'warning'}>
                                              {log.status}
                                            </Tag>
                                          )}
                                        </div>
                                        <Text type="secondary" className="text-sm">
                                          {dayjs(log.startTime).format('MMM D, YYYY h:mm A')}
                                        </Text>
                                      </div>

                                      {/* Duration and Cost */}
                                      <div className="flex gap-4 mb-2 text-gray-600">
                                        <span>Duration: {log.duration}s</span>
                                        {log.billDuration && (
                                          <span>Cost: ${log.totalCost}</span>
                                        )}
                                      </div>

                                      {/* From/To Details */}
                                      {showFrom && (
                                        <div className="text-sm text-gray-700">
                                          From: <Text strong>{log.from}</Text>
                                        </div>
                                      )}
                                      {showTo && (
                                        <div className="text-sm text-gray-600 mt-1">
                                          To: <Text type="secondary">{log.to}</Text>
                                        </div>
                                      )}
                                      {showHangup && (
                                        <div className="text-sm text-gray-500 mt-1">
                                          Hangup: <Text type="secondary">{log.hangupCause}</Text>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </Card>
                              );
                            })}
                          </div>
                        ) : (
                          <Empty description="No call logs found" />
                        )}
                      </div>
                    ),
                  },
                  {
                    key: "notes",
                    label: "Notes",
                    children: (
                      <div
                        className="space-y-4"
                        style={{
                          maxHeight: "500px",
                          overflowY: "auto",
                          paddingRight: "10px",
                        }}
                      >
                        <div className="flex justify-between items-center">
                          <Title level={5} className="mb-0">
                            Notes
                          </Title>
                          <Button
                            type="primary"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => setNoteModalVisible(true)}
                            className="focus-visible:focus"
                          >
                            Add Note
                          </Button>
                        </div>

                        <List
                          dataSource={currentDeal.notes || []}
                          locale={{ emptyText: "No notes added yet" }}
                          renderItem={(note) => (
                            <List.Item
                              actions={[
                                <Button
                                  key="edit"
                                  type="text"
                                  size="small"
                                  icon={<EditOutlined />}
                                  onClick={() => handleEditItem("note", note)}
                                />,
                                <Button
                                  key="delete"
                                  type="text"
                                  size="small"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() =>
                                    handleDeleteItem("note", note._id)
                                  }
                                />,
                              ]}
                            >
                              <List.Item.Meta
                                avatar={<Avatar icon={<FileTextOutlined />} />}
                                title={
                                  <div className="flex justify-between">
                                    <Text strong>Note</Text>
                                    <Text type="secondary" className="text-sm">
                                      {dayjs(note.createdAt).format(
                                        "MMM DD, YYYY HH:mm"
                                      )}
                                    </Text>
                                  </div>
                                }
                                description={
                                  <Paragraph>{note.content}</Paragraph>
                                }
                              />
                            </List.Item>
                          )}
                        />
                      </div>
                    ),
                  },
                  {
                    key: "documents",
                    label: "Documents",
                    children: (
                      <div
                        className="space-y-4"
                        style={{
                          maxHeight: "500px",
                          overflowY: "auto",
                          paddingRight: "10px",
                        }}
                      >
                        <div className="flex justify-between items-center mb-4">
                          <Title level={5} className="mb-0">
                            Documents
                          </Title>
                          <Space>
                            <Button
                              type="default"
                              icon={<FilePdfOutlined />}
                              onClick={() => setProposalDrawerVisible(true)}
                              size="small"
                            >
                              Create Proposal
                            </Button>
                            <Button
                              type="primary"
                              size="small"
                              icon={<PlusOutlined />}
                              onClick={() => setDocumentModalVisible(true)}
                              className="focus-visible:focus"
                            >
                              Upload Document
                            </Button>
                          </Space>
                        </div>

                        <Upload.Dragger
                          name="files"
                          multiple
                          action="/api/upload"
                          onChange={(info) => {
                            if (info.file.status === "done") {
                              messageApi.success(
                                `${info.file.name} file uploaded successfully`
                              );
                            } else if (info.file.status === "error") {
                              messageApi.error(
                                `${info.file.name} file upload failed.`
                              );
                            }
                          }}
                        >
                          <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                          </p>
                          <p className="ant-upload-text">
                            Click or drag file to this area to upload
                          </p>
                          <p className="ant-upload-hint">
                            Support for single or bulk upload. Documents will be
                            attached to this deal.
                          </p>
                        </Upload.Dragger>

                        <List
                          dataSource={currentDeal.documents || []}
                          locale={{ emptyText: "No documents uploaded" }}
                          renderItem={(doc) => (
                            <List.Item
                              actions={[
                                <Button
                                  key="download"
                                  type="link"
                                  icon={<DownloadOutlined />}
                                >
                                  Download
                                </Button>,
                              ]}
                            >
                              <List.Item.Meta
                                avatar={<Avatar icon={<FileOutlined />} />}
                                title={doc.name}
                                description={`${doc.size} • ${dayjs(
                                  doc.uploadedAt
                                ).format("MMM DD, YYYY")}`}
                              />
                            </List.Item>
                          )}
                        />
                      </div>
                    ),
                  },
                  {
                    key: "tasks",
                    label: "Tasks",
                    children: (
                      <div
                        style={{
                          padding: "20px",
                          maxHeight: "500px",
                          overflowY: "auto",
                        }}
                      >
                        <div
                          style={{
                            marginBottom: "16px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <h4>Tasks ({currentDeal?.tasks?.length || 0})</h4>
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            size="small"
                            onClick={handleAddTask}
                          >
                            Add Task
                          </Button>
                        </div>

                        {!currentDeal?.tasks ||
                        currentDeal.tasks.length === 0 ? (
                          <Empty
                            description="No tasks found"
                            style={{ margin: "40px 0" }}
                          />
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "12px",
                            }}
                          >
                            {currentDeal.tasks.map((task, index) => (
                              <Card
                                key={task._id || index}
                                size="small"
                                style={{ border: "1px solid #f0f0f0" }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <div style={{ flex: 1 }}>
                                    <div
                                      style={{
                                        fontWeight: "bold",
                                        marginBottom: "8px",
                                      }}
                                    >
                                      {task.title}
                                    </div>
                                    {task.description && (
                                      <div
                                        style={{
                                          color: "#666",
                                          marginBottom: "8px",
                                          fontSize: "14px",
                                        }}
                                      >
                                        {task.description}
                                      </div>
                                    )}
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: "8px",
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      <Tag
                                        color={
                                          task.priority === "high"
                                            ? "red"
                                            : task.priority === "medium"
                                            ? "orange"
                                            : "blue"
                                        }
                                      >
                                        {task.priority?.toUpperCase() ||
                                          "MEDIUM"}
                                      </Tag>
                                      <Tag
                                        color={
                                          task.status === "completed"
                                            ? "green"
                                            : task.status === "in_progress"
                                            ? "blue"
                                            : "orange"
                                        }
                                      >
                                        {task.status
                                          ?.replace("_", " ")
                                          .toUpperCase() || "PENDING"}
                                      </Tag>
                                      {task.dueDate && (
                                        <Tag color="purple">
                                          Due:{" "}
                                          {new Date(
                                            task.dueDate
                                          ).toLocaleDateString()}
                                        </Tag>
                                      )}
                                    </div>
                                  </div>
                                  <div style={{ display: "flex", gap: "8px" }}>
                                    <Button
                                      size="small"
                                      type="link"
                                      onClick={() => handleEditTask(task)}
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      size="small"
                                      type="link"
                                      danger
                                      onClick={() => handleDeleteTask(task._id)}
                                    >
                                      Delete
                                    </Button>
                                  </div>
                                </div>
                              </Card>
                            ))}
                          </div>
                        )}
                      </div>
                    ),
                  },
                  {
                    key: "email",
                    label: (
                      <span>
                        <MailOutlined /> Email
                      </span>
                    ),
                    children: (
                      <Tabs
                        defaultActiveKey="compose"
                        items={[
                          {
                            key: "compose",
                            label: "Compose & Send",
                            children: (
                              <div style={{ maxHeight: "550px", overflowY: "auto", padding: "16px" }}>
                                <EmailCompose
                                  leadId={currentDeal?._id}
                                  leadEmail={currentDeal?.contact?.email}
                                  leadName={`${currentDeal?.contact?.firstName || ''} ${currentDeal?.contact?.lastName || ''}`.trim() || currentDeal?.title}
                                  onEmailSent={() => {
                                    setEmailRefreshTrigger(prev => prev + 1);
                                    fetchDealById(id);
                                    messageApi.success('Email sent! Check the History tab to view.');
                                  }}
                                />
                              </div>
                            )
                          },
                          {
                            key: "history",
                            label: "Email History",
                            children: (
                              <div style={{ padding: "16px" }}>
                                <EmailHistory
                                  leadId={currentDeal?._id}
                                  isDeal={true}
                                  refreshTrigger={emailRefreshTrigger}
                                />
                              </div>
                            )
                          }
                        ]}
                      />
                    ),
                  },
                  // Conditionally add WhatsApp tab based on environment variable
                  ...(isWhatsAppEnabled ? [{
                    key: "whatsapp",
                    label: (
                      <span>
                        <WhatsAppOutlined /> WhatsApp
                      </span>
                    ),
                    children: (
                      <div
                        style={{
                          height: "550px",
                          display: "flex",
                          flexDirection: "column",
                          background: "#f0f2f5",
                        }}
                      >
                        {/* Chat Header */}
                        <div
                          style={{
                            padding: "16px",
                            background: "#25D366",
                            color: "white",
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                          }}
                        >
                          <Avatar
                            size={40}
                            style={{ background: "#128C7E" }}
                            icon={<UserOutlined />}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: "bold", fontSize: "16px" }}>
                              {currentDeal.title || "Deal"}
                            </div>
                            <div style={{ fontSize: "12px", opacity: 0.9 }}>
                              {currentDeal?.contact?.phone || "No phone number"}
                            </div>
                          </div>
                        <Tag color="success">Active</Tag>
                      </div>

                      {whatsappUnavailable && (
                        <div style={{ padding: "12px 16px" }}>
                          <Alert
                            type="warning"
                            showIcon
                            message="WhatsApp integration is unavailable"
                            description="Please verify the WhatsApp configuration or try again later."
                          />
                        </div>
                      )}

                      {/* Chat Messages Area */}
                      <div
                        ref={chatContainerRef}
                          style={{
                            flex: 1,
                            overflowY: "auto",
                            padding: "16px",
                            backgroundImage:
                              "url('data:image/svg+xml,%3Csvg width=\"100\" height=\"100\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cpath d=\"M0 0h100v100H0z\" fill=\"%23e5ddd5\"/%3E%3Cpath d=\"M25 25l5 5-5 5-5-5z\" fill=\"%23d1d7db\" opacity=\".1\"/%3E%3C/svg%3E')",
                          }}
                        >
                          {isChatLoading ? (
                            <div className="flex justify-center items-center h-full">
                              <Spin size="large" />
                            </div>
                          ) : chatMessages.length === 0 ? (
                            <Empty
                              description="No messages yet"
                              image={Empty.PRESENTED_IMAGE_SIMPLE}
                              style={{ marginTop: "100px" }}
                            />
                          ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                              {chatMessages.map((msg) => (
                                <div
                                  key={msg._id}
                                  style={{
                                    display: "flex",
                                    justifyContent: msg.entity === "user" ? "flex-start" : "flex-end",
                                  }}
                                >
                                  <div
                                    style={{
                                      maxWidth: "70%",
                                      background: msg.entity === "user" ? "white" : "#dcf8c6",
                                      padding: "8px 12px",
                                      borderRadius: "8px",
                                      boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                    }}
                                  >
                                    <div style={{ fontSize: "14px", marginBottom: "4px" }}>
                                      {msg.message}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#667781",
                                        textAlign: "right",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "flex-end",
                                        gap: "4px",
                                      }}
                                    >
                                      {dayjs(msg.timestamp).format("HH:mm A")}
                                      {msg.entity === "bot" && (
                                        <CheckCircleOutlined style={{ fontSize: "12px", color: "#53bdeb" }} />
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Message Input Area */}
                        <div
                          style={{
                            padding: "12px 16px",
                            background: "#f0f2f5",
                            borderTop: "1px solid #e0e0e0",
                          }}
                        >
                          <div style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
                            <Input.TextArea
                              placeholder="Type a message..."
                              autoSize={{ minRows: 1, maxRows: 4 }}
                              style={{
                                flex: 1,
                                borderRadius: "20px",
                                padding: "8px 16px",
                              }}
                              value={chatInput}
                              onChange={(e) => setChatInput(e.target.value)}
                              onPressEnter={(e) => {
                                if (!e.shiftKey) {
                                  e.preventDefault();
                                  handleSendChatMessage();
                                }
                              }}
                              disabled={whatsappUnavailable}
                            />
                            <Button
                              type="primary"
                              shape="circle"
                              icon={<SendOutlined />}
                              size="large"
                              style={{
                                background: "#25D366",
                                borderColor: "#25D366",
                              }}
                              onClick={handleSendChatMessage}
                              disabled={whatsappUnavailable}
                            />
                          </div>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#667781",
                              marginTop: "8px",
                              textAlign: "center",
                            }}
                          >
                            {whatsappUnavailable
                              ? "WhatsApp messaging is currently unavailable"
                              : "Press Enter to send • Shift+Enter for new line"}
                          </div>
                        </div>
                      </div>
                    ),
                  }] : []),
                  // Conditionally add AI Calling tab based on environment variable
                  ...(isAICallingEnabled ? [{
                    key: "aicalling",
                    label: (
                      <span>
                        <CustomerServiceOutlined /> AI Calling
                      </span>
                    ),
                    children: (
                      <div
                        style={{
                          height: "550px",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {/* Call Header */}
                        <div
                          style={{
                            padding: "16px 20px",
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            color: "white",
                            borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                            <Avatar
                              size={48}
                              style={{ 
                                background: "rgba(255, 255, 255, 0.2)",
                                border: "2px solid white"
                              }}
                              icon={<CustomerServiceOutlined style={{ fontSize: "24px" }} />}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: "bold", fontSize: "16px", marginBottom: "2px" }}>
                                {currentDeal.title || "Deal"}
                              </div>
<div style={{ fontSize: "12px", opacity: 0.9 }}>
                              {currentDeal?.contact?.phone || "No phone number"}
                            </div>
                            </div>
                            <div style={{ display: "flex", gap: "8px" }}>
                              <Button
                                type="primary"
                                size="small"
                                icon={<PhoneFilled  className="icon-flip-horizontal"/>}
                                style={{
                                  background: "#52c41a",
                                  borderColor: "#52c41a",
                                }}
                                onClick={handleStartAICallCampaign}
                                loading={isInitiatingAICall}
                                disabled={isInitiatingAICall}
                              >
                                {isInitiatingAICall ? "Starting Call..." : "Start Call"}
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Single Column Layout for Call Transcript */}
                          <div
                            style={{
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              background: "#fafafa",
                            }}
                          >
                            <div
                              style={{
                                padding: "12px 16px",
                                background: "white",
                                borderBottom: "1px solid #f0f0f0",
                                fontWeight: "bold",
                                fontSize: "14px",
                              }}
                            >
                              <FileTextOutlined style={{ marginRight: "8px" }} />
                              Call Transcript
                            </div>
                            <div
                              style={{
                                flex: 1,
                                overflowY: "auto",
                                padding: "16px",
                              }}
                            >
                              {isTranscriptsLoading ? (
                                <div className="flex justify-center items-center h-full">
                                  <Spin size="large" />
                                </div>
                              ) : transcripts.length === 0 ? (
                                <Empty
                                  description="No call transcripts available for this deal."
                                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                                  style={{ marginTop: "100px" }}
                                />
                              ) : (
                                <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                                  {transcripts.map((call, callIndex) => (
                                    <div key={call.id || callIndex} style={{ border: "1px solid #e0e0e0", borderRadius: "8px", padding: "16px", background: "white" }}>
                                      <div style={{ marginBottom: "12px", paddingBottom: "8px", borderBottom: "1px solid #f0f0f0" }}>
                                        <Title level={5} style={{ margin: 0 }}>Call ID: {call.id}</Title>
                                        <Text type="secondary" style={{ fontSize: "12px" }}>
                                          Status: <Tag color={call.status === "completed" ? "green" : "orange"}>{call.status?.toUpperCase()}</Tag>
                                          Duration: {call.duration} seconds
                                          <br />
                                          Start: {dayjs(call.callStartDate).format("MMM DD, YYYY HH:mm:ss")}
                                          <br />
                                          End: {dayjs(call.callEndDate).format("MMM DD, YYYY HH:mm:ss")}
                                        </Text>
                                      </div>
                                      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                                        {call.transcript.map((entry, entryIndex) => (
                                          <div
                                            key={entryIndex}
                                            style={{
                                              display: "flex",
                                              justifyContent: entry.role === "assistant" ? "flex-end" : "flex-start",
                                            }}
                                          >
                                            <div
                                              style={{
                                                maxWidth: "70%",
                                                background: entry.role === "assistant" ? "#e6f7ff" : "#f5f5f5",
                                                padding: "10px 12px",
                                                borderRadius: "8px",
                                                borderLeft: entry.role === "assistant" ? "3px solid #1890ff" : "3px solid #d9d9d9",
                                                alignSelf: entry.role === "assistant" ? "flex-end" : "flex-start",
                                              }}
                                            >
                                              <div style={{ fontSize: "11px", color: "#999", marginBottom: "4px", textAlign: entry.role === "assistant" ? "right" : "left" }}>
                                                {entry.role === "assistant" ? "AI Assistant" : (currentDeal.title || "Contact")}
                                              </div>
                                              <div>
                                                {entry.content}
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                      </div>
                    ),
                  }] : []),
                ]}
              />
            </Card>
          </Col>

          {/* Column 3: Timeline (18%) */}
          <Col span={5}>
            <Card className="shadow-soft h-full bg-white dark:bg-gray-800">
              <Title level={5} className="mb-4">
                Deal Timeline
              </Title>
              <Timeline
                items={[
                  {
                    color: "green",
                    children: (
                      <div>
                        <Text strong>Deal Created</Text>
                        <br />
                        <Text type="secondary" className="text-sm">
                          {dayjs(currentDeal.createdAt).format(
                            "MMM DD, YYYY HH:mm"
                          )}
                        </Text>
                        <br />
                        <Text className="text-sm">
                          Deal value: {formatCurrency(currentDeal.value)}
                        </Text>
                      </div>
                    ),
                  },
                  ...(currentDeal.activities || []).map((activity, index) => ({
                    color:
                      activity.type === "call"
                        ? "blue"
                        : activity.type === "email"
                        ? "orange"
                        : "purple",
                    children: (
                      <div key={`activity-${index}`}>
                        <Text strong>
                          {activity.type?.toUpperCase()}: {activity.subject}
                        </Text>
                        <br />
                        <Text type="secondary" className="text-sm">
                          {dayjs(activity.date).format("MMM DD, YYYY HH:mm")}
                        </Text>
                        <br />
                        <Text className="text-sm">{activity.description}</Text>
                      </div>
                    ),
                  })),
                  ...(currentDeal.tasks || []).map((task, index) => ({
                    color: "cyan",
                    children: (
                      <div key={`task-${index}`}>
                        <Text strong>Task: {task.title}</Text>
                        <br />
                        <Text type="secondary" className="text-sm">
                          {dayjs(task.createdAt || task.dueDate).format("MMM DD, YYYY HH:mm")}
                        </Text>
                        <br />
                        <Text className="text-sm">
                          {task.description || `Priority: ${task.priority}, Status: ${task.status}`}
                        </Text>
                      </div>
                    ),
                  })),
                  {
                    color: "orange",
                    children: (
                      <div>
                        <Text strong>Follow-up Required</Text>
                        <br />
                        <Text type="secondary" className="text-sm">
                          {currentDeal.expectedCloseDate
                            ? dayjs(currentDeal.expectedCloseDate).format(
                                "MMM DD, YYYY"
                              )
                            : "No date set"}
                        </Text>
                        <br />
                        <Text className="text-sm">Next action needed</Text>
                      </div>
                    ),
                  },
                ]}
              />
            </Card>
          </Col>
        </Row>

        {/* Add Note Modal */}
        <Modal
          title="Add Note"
          open={noteModalVisible}
          onCancel={() => setNoteModalVisible(false)}
          footer={null}
        >
          <Form form={noteForm} onFinish={handleAddNote} layout="vertical">
            <Form.Item
              name="content"
              label="Note Content"
              rules={[{ required: true, message: "Please enter note content" }]}
            >
              <TextArea rows={4} placeholder="Enter your note here..." />
            </Form.Item>
            <Form.Item className="mb-0 text-right">
              <Space>
                <Button onClick={() => setNoteModalVisible(false)}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit">
                  Add Note
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Add Activity Modal */}
        <Modal
          title="Add Activity"
          open={activityModalVisible}
          onCancel={() => setActivityModalVisible(false)}
          footer={null}
        >
          <Form
            form={activityForm}
            onFinish={handleAddActivity}
            layout="vertical"
          >
            <Form.Item
              name="type"
              label="Activity Type"
              rules={[
                { required: true, message: "Please select activity type" },
              ]}
            >
              <Select onChange={setActivityType}>
                <Option value="call">Call</Option>
                <Option value="email">Email</Option>
                <Option value="meeting">Meeting</Option>
                <Option value="task">Task</Option>
                <Option value="note">Note</Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="subject"
              label="Subject"
              rules={[{ required: true, message: "Please enter subject" }]}
            >
              <Input placeholder="Activity subject" />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <TextArea rows={3} placeholder="Activity description" />
            </Form.Item>
            <Form.Item name="scheduledAt" label="Scheduled Date">
              <DatePicker showTime className="w-full" />
            </Form.Item>
            <Form.Item className="mb-0 text-right">
              <Space>
                <Button onClick={() => setActivityModalVisible(false)}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit">
                  Add Activity
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Requirement Modal */}
        <Modal
          title={
            editingItem?.type === "requirement"
              ? "Edit Requirement"
              : "Add Requirement"
          }
          open={requirementModalVisible}
          onCancel={() => {
            setRequirementModalVisible(false);
            setEditingItem(null);
            requirementForm.resetFields();
          }}
          footer={null}
        >
          <Form
            form={requirementForm}
            onFinish={
              editingItem?.type === "requirement"
                ? handleUpdateRequirement
                : handleAddRequirement
            }
            layout="vertical"
          >
            <Form.Item
              name="title"
              label="Requirement Title"
              rules={[
                { required: true, message: "Please enter requirement title" },
              ]}
            >
              <Input placeholder="Enter requirement title" />
            </Form.Item>
            <Form.Item
              name="description"
              label="Description"
              rules={[{ required: true, message: "Please enter description" }]}
            >
              <TextArea rows={3} placeholder="Requirement description" />
            </Form.Item>
            <Form.Item
              name="priority"
              label="Priority"
              rules={[{ required: true, message: "Please select priority" }]}
            >
              <Select placeholder="Select priority">
                <Option value="low">Low</Option>
                <Option value="medium">Medium</Option>
                <Option value="high">High</Option>
                <Option value="critical">Critical</Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: "Please select status" }]}
            >
              <Select placeholder="Select status">
                <Option value="pending">Pending</Option>
                <Option value="in_progress">In Progress</Option>
                <Option value="completed">Completed</Option>
                <Option value="cancelled">Cancelled</Option>
              </Select>
            </Form.Item>
            <Form.Item className="mb-0 text-right">
              <Space>
                <Button
                  onClick={() => {
                    setRequirementModalVisible(false);
                    setEditingItem(null);
                    requirementForm.resetFields();
                  }}
                >
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit">
                  {editingItem?.type === "requirement" ? "Update" : "Add"}{" "}
                  Requirement
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Meeting Modal */}
        <Modal
          title={
            editingItem?.type === "meeting" ? "Edit Meeting" : "Add Meeting"
          }
          open={meetingModalVisible}
          onCancel={() => {
            setMeetingModalVisible(false);
            setEditingItem(null);
            meetingForm.resetFields();
          }}
          footer={null}
        >
          <Form
            form={meetingForm}
            onFinish={
              editingItem?.type === "meeting"
                ? handleUpdateMeeting
                : handleAddMeeting
            }
            layout="vertical"
          >
            <Form.Item
              name="title"
              label="Meeting Title"
              rules={[
                { required: true, message: "Please enter meeting title" },
              ]}
            >
              <Input placeholder="Enter meeting title" />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <TextArea rows={3} placeholder="Meeting description" />
            </Form.Item>
            <Form.Item
              name="scheduledAt"
              label="Scheduled Date & Time"
              rules={[
                { required: true, message: "Please select date and time" },
              ]}
            >
              <DatePicker showTime className="w-full" />
            </Form.Item>
            <Form.Item name="location" label="Location">
              <Input placeholder="Meeting location or video link" />
            </Form.Item>
            <Form.Item name="attendees" label="Attendees">
              <Input placeholder="Attendee names or emails" />
            </Form.Item>
            <Form.Item className="mb-0 text-right">
              <Space>
                <Button
                  onClick={() => {
                    setMeetingModalVisible(false);
                    setEditingItem(null);
                    meetingForm.resetFields();
                  }}
                >
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit">
                  {editingItem?.type === "meeting" ? "Update" : "Add"} Meeting
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Call Modal */}
        <Modal
          title={editingItem?.type === "call" ? "Edit Call" : "Add Call"}
          open={callModalVisible}
          onCancel={() => {
            setCallModalVisible(false);
            setEditingItem(null);
            callForm.resetFields();
          }}
          footer={null}
        >
          <Form
            form={callForm}
            onFinish={
              editingItem?.type === "call" ? handleUpdateCall : handleAddCall
            }
            layout="vertical"
          >
            <Form.Item
              name="type"
              label="Call Type"
              rules={[{ required: true, message: "Please select call type" }]}
            >
              <Select placeholder="Select call type">
                <Option value="inbound">Inbound</Option>
                <Option value="outbound">Outbound</Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="subject"
              label="Call Subject"
              rules={[{ required: true, message: "Please enter call subject" }]}
            >
              <Input placeholder="Enter call subject" />
            </Form.Item>
            <Form.Item
              name="notes"
              label="Call Notes"
              rules={[{ required: true, message: "Please enter call notes" }]}
            >
              <TextArea rows={4} placeholder="Call notes and outcomes" />
            </Form.Item>
            <Form.Item name="duration" label="Duration (minutes)">
              <Input type="number" placeholder="Call duration in minutes" />
            </Form.Item>
            <Form.Item name="outcome" label="Call Outcome">
              <Select placeholder="Select outcome">
                <Option value="connected">Connected</Option>
                <Option value="voicemail">Voicemail</Option>
                <Option value="no_answer">No Answer</Option>
                <Option value="busy">Busy</Option>
              </Select>
            </Form.Item>
            <Form.Item className="mb-0 text-right">
              <Space>
                <Button
                  onClick={() => {
                    setCallModalVisible(false);
                    setEditingItem(null);
                    callForm.resetFields();
                  }}
                >
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit">
                  {editingItem?.type === "call" ? "Update" : "Add"} Call
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Document Modal */}
        <Modal
          title="Add Document"
          open={documentModalVisible}
          onCancel={() => setDocumentModalVisible(false)}
          footer={null}
        >
          <Form onFinish={handleAddDocument} layout="vertical">
            <Form.Item
              name="name"
              label="Document Name"
              rules={[
                { required: true, message: "Please enter document name" },
              ]}
            >
              <Input placeholder="Enter document name" />
            </Form.Item>
            <Form.Item
              name="type"
              label="Document Type"
              rules={[
                { required: true, message: "Please select document type" },
              ]}
            >
              <Select placeholder="Select document type">
                <Option value="contract">Contract</Option>
                <Option value="proposal">Proposal</Option>
                <Option value="invoice">Invoice</Option>
                <Option value="presentation">Presentation</Option>
                <Option value="other">Other</Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="url"
              label="Document URL"
              rules={[{ required: true, message: "Please enter document URL" }]}
            >
              <Input placeholder="Enter document URL or file path" />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <TextArea rows={2} placeholder="Document description" />
            </Form.Item>
            <Form.Item className="mb-0 text-right">
              <Space>
                <Button onClick={() => setDocumentModalVisible(false)}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit">
                  Add Document
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Task Modal */}
        <Modal
          title={editingTask ? "Edit Task" : "Add Task"}
          open={taskModalVisible}
          onCancel={() => {
            setTaskModalVisible(false);
            taskForm.resetFields();
            setEditingTask(null);
          }}
          footer={null}
          width={600}
        >
          <Form form={taskForm} layout="vertical" onFinish={handleSaveTask}>
            <Form.Item
              name="title"
              label="Title"
              rules={[{ required: true, message: "Please enter task title" }]}
            >
              <Input placeholder="Enter task title" />
            </Form.Item>

            <Form.Item name="description" label="Description">
              <TextArea rows={3} placeholder="Enter task description" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="priority"
                  label="Priority"
                  initialValue="medium"
                >
                  <Select>
                    <Select.Option value="low">Low</Select.Option>
                    <Select.Option value="medium">Medium</Select.Option>
                    <Select.Option value="high">High</Select.Option>
                    <Select.Option value="urgent">Urgent</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="status" label="Status" initialValue="pending">
                  <Select>
                    <Select.Option value="pending">Pending</Select.Option>
                    <Select.Option value="in_progress">
                      In Progress
                    </Select.Option>
                    <Select.Option value="completed">Completed</Select.Option>
                    <Select.Option value="cancelled">Cancelled</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="dueDate" label="Due Date">
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item name="assignedTo" label="Assign To Team Member">
              <Select
                placeholder="Select team member"
                allowClear
                loading={loadingTeamMembers}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children
                    ?.toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              >
                {teamMembers.map((member) => (
                  <Select.Option key={member.id} value={member.id}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <Avatar size="small" src={member.avatar}>
                        {member.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <span>{member.name}</span>
                      <span style={{ color: "#999", fontSize: "12px" }}>
                        ({member.role})
                      </span>
                    </div>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="notes" label="Notes">
              <TextArea rows={2} placeholder="Additional notes" />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
              <Space>
                <Button
                  onClick={() => {
                    setTaskModalVisible(false);
                    taskForm.resetFields();
                    setEditingTask(null);
                  }}
                >
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit">
                  {editingTask ? "Update" : "Create"} Task
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Win Deal Modal */}
        <Modal
          title="Mark Deal as Won"
          open={winDealModalVisible}
          onCancel={() => {
            setWinDealModalVisible(false);
            winDealForm.resetFields();
          }}
          footer={null}
          width={600}
        >
          <Form form={winDealForm} layout="vertical" onFinish={handleWinDeal}>
            <Form.Item
              name="actualValue"
              label="Actual Deal Value"
              rules={[
                {
                  required: true,
                  message: "Please enter the actual deal value",
                },
              ]}
              initialValue={currentDeal?.value}
            >
              <InputNumber
                style={{ width: "100%" }}
                formatter={(value) =>
                  `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => value.replace(/₹\s?|(,*)/g, "")}
                placeholder="Enter actual deal value"
              />
            </Form.Item>

            <Form.Item
              name="reason"
              label="Reason for Winning"
              rules={[{ required: true, message: "Please select a reason" }]}
            >
              <Select placeholder="Select reason for winning">
                <Option value="best_price">Best Price</Option>
                <Option value="best_solution">Best Solution</Option>
                <Option value="relationship">Strong Relationship</Option>
                <Option value="timing">Perfect Timing</Option>
                <Option value="features">Superior Features</Option>
                <Option value="service">Excellent Service</Option>
                <Option value="other">Other</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="remarks"
              label="Closing Remarks"
              rules={[
                { required: true, message: "Please enter closing remarks" },
                { whitespace: true, message: "Please enter closing remarks" },
              ]}
            >
              <TextArea
                rows={4}
                placeholder="Enter details about how the deal was won, key factors, next steps, etc."
              />
            </Form.Item>

            <Form.Item className="mb-0">
              <Space className="w-full justify-end">
                <Button
                  onClick={() => {
                    setWinDealModalVisible(false);
                    winDealForm.resetFields();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="bg-green-600 hover:bg-green-700"
                  icon={<CheckCircleOutlined />}
                >
                  Mark as Won
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Lost Deal Modal */}
        <Modal
          title="Mark Deal as Lost"
          open={lostDealModalVisible}
          onCancel={() => {
            setLostDealModalVisible(false);
            lostDealForm.resetFields();
          }}
          footer={null}
          width={600}
        >
          <Form form={lostDealForm} layout="vertical" onFinish={handleLostDeal}>
            <Form.Item
              name="reason"
              label="Reason for Loss"
              rules={[
                { required: true, message: "Please select a reason for loss" },
              ]}
            >
              <Select placeholder="Select reason for losing">
                <Option value="price_too_high">Price Too High</Option>
                <Option value="competitor_chosen">Competitor Chosen</Option>
                <Option value="budget_constraints">Budget Constraints</Option>
                <Option value="timing_issues">Timing Issues</Option>
                <Option value="feature_mismatch">Feature Mismatch</Option>
                <Option value="no_decision">No Decision Made</Option>
                <Option value="internal_changes">Internal Changes</Option>
                <Option value="other">Other</Option>
              </Select>
            </Form.Item>

            <Form.Item name="competitor" label="Competitor (if applicable)">
              <Input placeholder="Enter competitor name if they won the deal" />
            </Form.Item>

            <Form.Item
              name="remarks"
              label="Loss Analysis & Remarks"
              rules={[
                {
                  required: true,
                  message: "Please enter loss analysis remarks",
                },
                {
                  whitespace: true,
                  message: "Please enter loss analysis remarks",
                },
              ]}
            >
              <TextArea
                rows={4}
                placeholder="Enter detailed analysis of why the deal was lost, lessons learned, feedback received, etc."
              />
            </Form.Item>

            <Form.Item className="mb-0">
              <Space className="w-full justify-end">
                <Button
                  onClick={() => {
                    setLostDealModalVisible(false);
                    lostDealForm.resetFields();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  danger
                  htmlType="submit"
                  icon={<CloseCircleOutlined />}
                >
                  Mark as Lost
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Assign Deal Modal */}
        <Modal
          title="Change Assignment"
          open={assignDealModalVisible}
          onCancel={() => {
            setAssignDealModalVisible(false);
            assignForm.resetFields();
          }}
          footer={null}
        >
          <Form
            form={assignForm}
            onFinish={handleAssignDeal}
            layout="vertical"
            initialValues={{
              assignedTo:
                currentDeal?.assignedTo?._id || currentDeal?.assignedTo,
            }}
          >
            <Form.Item
              name="assignedTo"
              label="Assign to Team Member"
              rules={[
                { required: true, message: "Please select a team member" },
              ]}
            >
              <Select
                placeholder="Select team member"
                loading={loadingTeamMembers}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children
                    ?.toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              >
                {teamMembers.map((member) => (
                  <Option key={member.id} value={member.id}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <Avatar size="small" src={member.avatar}>
                        {member.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <span>{member.name}</span>
                      <span style={{ color: "#999", fontSize: "12px" }}>
                        ({member.role})
                      </span>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item>
              <Space>
                <Button
                  onClick={() => {
                    setAssignDealModalVisible(false);
                    assignForm.resetFields();
                  }}
                >
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit">
                  Update Assignment
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Proposal Generation Drawer */}
        <Drawer
          title="Create Proposal"
          placement="right"
          width={600}
          onClose={() => {
            setProposalDrawerVisible(false);
            proposalForm.resetFields();
          }}
          open={proposalDrawerVisible}
          footer={
            <div style={{ textAlign: 'right' }}>
              <Space>
                <Button
                  onClick={() => {
                    setProposalDrawerVisible(false);
                    proposalForm.resetFields();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={() => proposalForm.submit()}
                  loading={isGeneratingProposal}
                  icon={<FilePdfOutlined />}
                >
                  Generate Proposal
                </Button>
              </Space>
            </div>
          }
        >
          <Form
            form={proposalForm}
            layout="vertical"
            onFinish={handleGenerateProposal}
            initialValues={{
              proposalTitle: `Business Proposal for ${currentDeal?.title || 'Deal'}`,
              proposalType: 'business',
              validityDays: 30,
              includeCompanyProfile: true
            }}
          >
            <Form.Item
              name="proposalTitle"
              label="Proposal Title"
              rules={[{ required: true, message: 'Please enter proposal title' }]}
            >
              <Input placeholder="Enter proposal title" />
            </Form.Item>

            <Form.Item
              name="proposalType"
              label="Proposal Type"
              rules={[{ required: true, message: 'Please select proposal type' }]}
            >
              <Select placeholder="Select proposal type">
                <Option value="business">Business Proposal</Option>
                <Option value="project">Project Proposal</Option>
                <Option value="service">Service Proposal</Option>
                <Option value="product">Product Proposal</Option>
                <Option value="partnership">Partnership Proposal</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="validityDays"
              label="Validity Period (Days)"
              rules={[{ required: true, message: 'Please enter validity period' }]}
            >
              <InputNumber
                min={1}
                max={365}
                style={{ width: '100%' }}
                placeholder="Enter validity in days"
              />
            </Form.Item>

            <Form.Item
              name="estimatedValue"
              label={`Estimated Value (${currencySymbol})`}
              rules={[{ required: true, message: 'Please enter estimated value' }]}
            >
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder="Enter estimated value"
                formatter={value => `${currencySymbol} ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value.replace(/₹\s?|(,*)/g, '')}
              />
            </Form.Item>

            <Form.Item
              name="deliveryTimeline"
              label="Delivery Timeline"
              rules={[{ required: true, message: 'Please enter delivery timeline' }]}
            >
              <Input placeholder="e.g., 4-6 weeks, 3 months" />
            </Form.Item>

            <Form.Item
              name="keyServices"
              label="Key Services/Products"
              rules={[{ required: true, message: 'Please enter key services' }]}
            >
              <TextArea
                rows={4}
                placeholder="Enter key services or products to be included in the proposal (one per line)"
              />
            </Form.Item>

            <Form.Item
              name="specialTerms"
              label="Special Terms & Conditions"
            >
              <TextArea
                rows={3}
                placeholder="Enter any special terms, conditions, or notes (optional)"
              />
            </Form.Item>

            <Form.Item
              name="includeCompanyProfile"
              label="Include Company Profile"
            >
              <Select defaultValue={true}>
                <Option value={true}>Yes</Option>
                <Option value={false}>No</Option>
              </Select>
            </Form.Item>

            <div style={{ 
              background: '#f0f2f5', 
              padding: '12px', 
              borderRadius: '4px',
              marginTop: '16px'
            }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                <strong>Note:</strong> The AI will automatically include deal details and create a comprehensive proposal based on the information provided above.
              </Text>
            </div>
          </Form>
        </Drawer>

        {/* Proposal Preview Modal */}
        <Modal
          title="Proposal Preview - Review & Edit"
          open={proposalPreviewVisible}
          onCancel={() => {
            setProposalPreviewVisible(false);
            setProposalDrawerVisible(true);
          }}
          width="95%"
          style={{ top: 20, maxWidth: '1600px' }}
          footer={[
            <Button
              key="back"
              onClick={() => {
                setProposalPreviewVisible(false);
                setProposalDrawerVisible(true);
              }}
            >
              Back to Edit Form
            </Button>,
            <Button
              key="generate"
              type="primary"
              loading={isGeneratingProposal}
              onClick={handleConfirmProposal}
              icon={<FilePdfOutlined />}
            >
              Confirm & Generate PDF
            </Button>
          ]}
        >
          {proposalPreviewData && (
            <Row gutter={24}>
              <Col span={10}>
                <div className="space-y-4" style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto', paddingRight: '12px' }}>
                  <Card 
                    title={
                      <Space>
                        <UserOutlined />
                        <span>Company Information (From)</span>
                      </Space>
                    }
                    size="small"
                    className="shadow-sm"
                  >
                    <Form layout="vertical" size="small">
                      <Form.Item label="Company Name" style={{ marginBottom: '12px' }}>
                        <Input
                          value={editedCompanyInfo?.companyName}
                          onChange={(e) => setEditedCompanyInfo({...editedCompanyInfo, companyName: e.target.value})}
                          placeholder="Enter company name"
                        />
                      </Form.Item>
                      <Form.Item label="Contact Person" style={{ marginBottom: '12px' }}>
                        <Input
                          value={editedCompanyInfo?.yourName}
                          onChange={(e) => setEditedCompanyInfo({...editedCompanyInfo, yourName: e.target.value})}
                          placeholder="Enter your name"
                        />
                      </Form.Item>
                      <Form.Item label="Email" style={{ marginBottom: '12px' }}>
                        <Input
                          value={editedCompanyInfo?.email}
                          onChange={(e) => setEditedCompanyInfo({...editedCompanyInfo, email: e.target.value})}
                          placeholder="Enter email"
                        />
                      </Form.Item>
                      <Form.Item label="Phone" style={{ marginBottom: '12px' }}>
                        <Input
                          value={editedCompanyInfo?.phone}
                          onChange={(e) => setEditedCompanyInfo({...editedCompanyInfo, phone: e.target.value})}
                          placeholder="Enter phone"
                        />
                      </Form.Item>
                    </Form>
                  </Card>

                  <Card 
                    title={
                      <Space>
                        <MailOutlined />
                        <span>Prepared For (Client)</span>
                      </Space>
                    }
                    size="small"
                    className="shadow-sm"
                  >
                    <Descriptions column={1} size="small" bordered>
                      <Descriptions.Item label="Company">{proposalPreviewData.dealInfo?.title}</Descriptions.Item>
                      <Descriptions.Item label="Contact Person">{proposalPreviewData.dealInfo?.contactPerson}</Descriptions.Item>
                      <Descriptions.Item label="Email">{proposalPreviewData.dealInfo?.email}</Descriptions.Item>
                      <Descriptions.Item label="Phone">{proposalPreviewData.dealInfo?.phone}</Descriptions.Item>
                    </Descriptions>
                  </Card>

                  <Card 
                    title={
                      <Space>
                        <CalendarOutlined />
                        <span>Proposal Details</span>
                      </Space>
                    }
                    size="small"
                    className="shadow-sm"
                  >
                    <Descriptions column={1} size="small" bordered>
                      <Descriptions.Item label="Title">{proposalPreviewData.proposalData?.proposalTitle}</Descriptions.Item>
                      <Descriptions.Item label="Validity">{proposalPreviewData.proposalData?.validityDays} days</Descriptions.Item>
                      <Descriptions.Item label="Estimated Value">
                        <Text strong style={{ color: '#52c41a' }}>
                          {formatCurrency(proposalPreviewData.proposalData?.estimatedValue)}
                        </Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="Delivery Timeline">{proposalPreviewData.proposalData?.deliveryTimeline}</Descriptions.Item>
                    </Descriptions>
                  </Card>
                </div>
              </Col>

              <Col span={14}>
                <Card
                  title="Proposal Content - Edit as needed"
                  size="small"
                  className="shadow-sm"
                  styles={{ body: { maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' } }}
                >
                  <TextArea
                    value={editedProposalContent}
                    onChange={(e) => setEditedProposalContent(e.target.value)}
                    rows={30}
                    placeholder="Edit proposal content here"
                  />
                </Card>
              </Col>
            </Row>
          )}
        </Modal>

        <Modal
          title="Select Phone Number for AI Call"
          open={aiCallPhoneModalVisible}
          onCancel={() => setAICallPhoneModalVisible(false)}
          onOk={handleConfirmAICallPhoneSelection}
          okText="Start AI Call"
          cancelText="Cancel"
          width={600}
          confirmLoading={isInitiatingAICall}
        >
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              Select Country:
            </label>
            <Select
              value={aiCallSelectedCountryIndex}
              onChange={(value) => {
                setAICallSelectedCountryIndex(value);
                setAICallSelectedPhoneIndex(0);
              }}
              style={{ width: '100%' }}
            >
              {callSettings?.plivoSettings?.countries?.map((country, index) => (
                <Option key={index} value={index}>
                  {country.countryName} ({country.countryCode})
                </Option>
              ))}
            </Select>
          </div>

          {callSettings?.plivoSettings?.countries?.[aiCallSelectedCountryIndex] && (
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Select Phone Number:
              </label>
              <Select
                value={aiCallSelectedPhoneIndex}
                onChange={setAICallSelectedPhoneIndex}
                style={{ width: '100%' }}
              >
                {callSettings.plivoSettings.countries[aiCallSelectedCountryIndex].phoneNumbers?.map((phone, index) => (
                  <Option key={index} value={index}>
                    {phone}
                  </Option>
                ))}
              </Select>

              <Card
                style={{ marginTop: '16px', backgroundColor: '#f5f5f5' }}
                size="small"
              >
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Country">
                    {callSettings.plivoSettings.countries[aiCallSelectedCountryIndex].countryName}
                  </Descriptions.Item>
                  <Descriptions.Item label="Country Code">
                    {callSettings.plivoSettings.countries[aiCallSelectedCountryIndex].countryCode}
                  </Descriptions.Item>
                  <Descriptions.Item label="Selected Phone">
                    {callSettings.plivoSettings.countries[aiCallSelectedCountryIndex].phoneNumbers[aiCallSelectedPhoneIndex]}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </div>
          )}
        </Modal>

        {/* Phone Selection Modal for Manual Calling */}
        <PhoneSelectionModal
          visible={phoneSelectionModalVisible}
          onCancel={closePhoneSelectionModal}
          onConfirm={() => {
            const phoneNumber = currentDeal?.contact?.phone;
            if (!phoneNumber) {
              messageApi.error("No phone number available for this deal.");
              return;
            }
            initiateCall(phoneNumber);
          }}
          callSettings={callSettings}
          selectedCountryIndex={selectedCountryIndex}
          onCountryChange={(value) => {
            setSelectedCountryIndex(value);
            setSelectedPhoneIndex(0);
          }}
          selectedPhoneIndex={selectedPhoneIndex}
          onPhoneChange={setSelectedPhoneIndex}
          loading={isManualCalling}
        />
      </div>
    );
  }

  return (
    <Spin size="large" className="flex justify-center items-center h-64" />
  );
};

export default DealDetailView;
